/**
 * The TWO order-level note fields — their labels and their ⓘ tooltips, in ONE
 * place, imported by every surface that renders either.
 *
 * Owner ruling (Pete, 2026-09-13): *"lets move it to the order details section,
 * and update the tool tip hints about printed/not printed. and update the label
 * as you recommended."* The recommendation he accepted: `Admin Notes` becomes
 * **`Internal notes`** (sentence case), `Notes / Comments` gains *"Printed on
 * the order."* and the internal field gains *"Not printed. Visible to everyone
 * in the department who can open this order."*
 *
 * 🔑 **THE TWO STRINGS ARE A PAIR AND THE PAIR IS THE POINT.** Neither tooltip
 * says anything useful alone — what a user needs to know is which of the two
 * boxes in front of them reaches the supplier. Retyping either at a call site
 * is how they come to disagree, so both live here and every surface imports
 * them: `components/OrderForm.js` (Notes / Comments, both branches),
 * `components/InternalNotesReadOnly.js` (the three read-only pages),
 * `pages/DepartmentOrders.js` (the manager's editor) and `pages/OrderPage.js`.
 *
 * 🚨 **`INTERNAL_NOTES_TOOLTIP` IS A CLAIM ABOUT TWO OTHER FILES AND IT IS ONLY
 * TRUE WHILE BOTH HOLD.** "Not printed" is true because
 * `client/src/utils/orderPdf.js` emits the block ONLY when its `isAdmin`
 * argument is set, i.e. never on the supplier PDF. "Visible to everyone … who
 * can open this order" is true because `orders.admin_notes` is stripped from no
 * payload — `GET /api/orders/:id` returns the whole row — so every viewer of
 * the order reads it. **Neither sentence is a permission gate; both are
 * DESCRIPTIONS.** If a future change strips the column from a payload, or
 * prints it for a supplier, this string becomes a lie that nothing will fail.
 *
 * ⚠️ **THE LABEL CHANGE IS COSMETIC AND CHANGES NOTHING ABOUT WHO MAY READ OR
 * WRITE THE FIELD.** The column is still `orders.admin_notes`, still written by
 * `POST /api/orders/:id/notes` behind `canManageDepartmentOrders` in the
 * order's own department, still audited as `notes_updated`, and the server's
 * own audit wording ("Admin notes updated") is deliberately untouched — that is
 * historical record, not a label.
 */

/** The field label, sentence case. Rendered with a trailing `:` on the read-only surfaces. */
export const INTERNAL_NOTES_LABEL = 'Internal notes';

/** ⓘ on `Notes / Comments` — the field that DOES reach the supplier. */
export const ORDER_NOTES_TOOLTIP = 'Printed on the order.';

/** ⓘ on `Internal notes` — the field that does not. */
export const INTERNAL_NOTES_TOOLTIP =
  'Not printed. Visible to everyone in the department who can open this order.';

/**
 * The editor's placeholder. Singular ("a note"), because the field reads as one
 * remark about this order rather than a log; the old copy was `Add admin
 * notes...` with three ASCII periods, and the ellipsis here is the single
 * character, matching the rest of this app's placeholders.
 */
export const INTERNAL_NOTES_PLACEHOLDER = 'Add an internal note…';
